import React from 'react';
import { useAtom, useAtomValue } from 'jotai';
import Tooltip from '@mui/material/Tooltip';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Stack, SxProps, Typography, Box } from '@mui/material';
import * as poiState from '@/components/Analysis/modules/pointOfInterest/state';
import { generatePoiDisplayName } from './utils';
import { PoiStatus, PointOfInterest } from '@/__generated__/graphql';
import { Navigation } from '@/components/shared/Navigation/Navigation';
import { NavigationItem } from '@/types';
import { abyssColors } from '@/theme/colors';
import { LocationTag } from '@/constants';

type Props = { styleSx?: SxProps; selectedPoiInfo?: PointOfInterest; enableNavigation?: boolean };

export const PoiNavigation = ({ selectedPoiInfo, styleSx, enableNavigation = false }: Props) => {
  const [selectedPoiId, setSelectedPoiId] = useAtom(poiState.selectedPointOfInterestId);
  const poisInComponent = useAtomValue(poiState.poiFilteredByCheckbox);
  const poisData =
    poisInComponent?.map((poi) => ({
      id: poi.id,
      name: poi.name ?? '',
    })) ?? [];

  const handleChange = (poiData: Partial<NavigationItem>) => {
    setSelectedPoiId(poiData.id);
  };

  let statusIcon;
  let displayName;

  if (selectedPoiInfo) {
    const { name, poiId, type, status, isVerified } = selectedPoiInfo;

    displayName = generatePoiDisplayName({ name, poiId, poiType: type });

    const iconProps = { sx: { ml: 1 } };

    if (status && status === PoiStatus.Completed) {
      statusIcon = (
        <Tooltip title="Completed">
          <CheckCircleIcon {...iconProps} sx={{ ...iconProps.sx, color: 'success.main' }} />
        </Tooltip>
      );
    } else if (isVerified) {
      statusIcon = (
        <Tooltip title="Verified">
          <CheckCircleIcon {...iconProps} sx={{ ...iconProps.sx, color: '#0B9DEC' }} />
        </Tooltip>
      );
    }
  }

  const poiInfoContent = (
    <Box sx={{ ...(enableNavigation ? styleSx : {}), paddingLeft: enableNavigation ? 0 : '1.5rem' }}>
      <Stack className="poiNavigation" direction="row" alignItems="center">
        <Typography
          sx={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: 200,
            color: abyssColors.primary[700],
            fontWeight: '700',
          }}
        >
          {displayName}
        </Typography>
        {statusIcon}
      </Stack>
    </Box>
  );

  if (enableNavigation) {
    return (
      <Navigation
        items={poisData}
        toolTip={displayName}
        selectedItemId={selectedPoiId}
        handleChange={handleChange}
        location={LocationTag.ViewerSelectedPoi}
      >
        {poiInfoContent}
      </Navigation>
    );
  }

  return poiInfoContent;
};
